import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /** @type {string | null} Query represented by the actionable results; null while invalidated. */
  #renderedQuery = null;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogStateChange, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogStateChange, { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    this.#decorateOptions();
    this.#syncSearchTriggers();

    // No idle prefetch. `snippets/search-modal.liquid` renders an empty listbox, so
    // there is nothing to reconcile on load, and prefetching here would spend a
    // Section Rendering API request on every page view for a dialog most visitors
    // never open. The list is fetched on first open instead — see `#handleDialogOpen`.
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#activeFetch?.abort();
    this.#debouncedSearch.cancel();
    this.resetSearch.cancel();
    this.#emptyStateLoaded = false;
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#debouncedSearch.cancel();
    this.resetSearch.cancel();
    this.#activeFetch?.abort();
    this.#invalidateResults();
    this.refs.searchInput.value = '';
    this.#hideResetButton();
    this.#emptyStateLoaded = false;
  };

  /**
   * Fetches default results on open. Closing aborts pending work, and a failed
   * initial fetch can be retried on the next open without an idle prefetch.
   */
  #handleDialogOpen = () => {
    this.#loadEmptyState();
  };

  /**
   * Keeps the combobox and its triggers in step with the dialog opening and closing.
   */
  #handleDialogStateChange = () => {
    this.#syncExpandedState();
    this.#syncSearchTriggers();
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    if (this.refs.searchInput.value.trim()) {
      this.search();
      return;
    }

    // `#resetSearch` directly, not the debounced public `resetSearch`. The debounced
    // path clears the input 100ms later, which would wipe anything typed in the first
    // moments after the dialog opens. Called synchronously on open the input is still
    // empty, and if the visitor types during the fetch, `#getSearchResults` aborts
    // this request and `#resetSearch` bails before morphing.
    this.#resetSearch();
  }

  /**
   * The native dialog the search modal renders inside, when there is one.
   * @returns {HTMLDialogElement | null}
   */
  get #dialogElement() {
    return /** @type {HTMLDialogElement | null} */ (this.closest('dialog'));
  }

  /**
   * Whether the listbox popup is actually on screen. Outside a dialog the popup is
   * inline and always visible when it holds options.
   * @returns {boolean}
   */
  get #isPopupVisible() {
    const dialogElement = this.#dialogElement;

    return dialogElement ? dialogElement.open : true;
  }

  /**
   * Applies the listbox-option contract to every rendered result.
   *
   * Results come back from the Section Rendering API and are morphed in, so this
   * runs after every render. `role="option"` is also in the Liquid markup, which is
   * what survives a re-render on its own; the ids are assigned here because one
   * popup can hold two renders of the same snippet — `#resetSearch` prepends the
   * recently-viewed list into the empty state — so a `forloop`-derived id would not
   * be unique. Carousel results come from `slideshow-slide.liquid`, which this
   * phase does not own, so for those this is the only place the role is set.
   */
  #decorateOptions() {
    for (const [index, item] of this.#allResultsItems.entries()) {
      item.setAttribute('role', 'option');
      item.id = `predictive-search-option-${index}`;
    }

    this.#syncExpandedState();
  }

  /**
   * Toggles `aria-expanded` on the combobox: true only while a populated listbox is
   * showing. Replaces the hard-coded `aria-expanded` the empty-state markup carried.
   */
  #syncExpandedState() {
    const { searchInput } = this.refs;
    const expanded = this.#isPopupVisible && this.#hasCurrentResults && this.#allResultsItems.length > 0;

    searchInput.setAttribute('aria-expanded', expanded ? 'true' : 'false');

    if (!expanded) this.#setActiveDescendant(null);
  }

  get #hasCurrentResults() {
    return this.#renderedQuery !== null && this.#renderedQuery === this.refs.searchInput.value.trim();
  }

  // Invalidate synchronously, before debounce or fetch. Old cards must not remain
  // clickable or become an Enter destination for text the shopper just changed.
  #invalidateResults() {
    this.#renderedQuery = null;
    this.#setActiveDescendant(null);
    for (const item of this.#allResultsItems) {
      item.removeAttribute('aria-selected');
      item.classList.remove('keyboard-focus');
    }
    this.refs.predictiveSearchResults.inert = true;
    this.refs.predictiveSearchResults.style.visibility = 'hidden';
    this.#syncExpandedState();
  }

  #revealResults(query) {
    this.#renderedQuery = query;
    this.refs.predictiveSearchResults.inert = false;
    this.refs.predictiveSearchResults.style.removeProperty('visibility');
    this.#decorateOptions();
    this.#resetScrollPositions();
  }

  /**
   * Mirrors the dialog's open state onto every header search trigger, the way
   * `header-actions.js` does for the cart-drawer trigger. The trigger markup lives
   * in `snippets/search.liquid` and points `aria-controls` at this dialog's id.
   */
  #syncSearchTriggers() {
    const dialogComponent = this.dialog;

    if (!dialogComponent?.id) return;

    const expanded = this.#isPopupVisible ? 'true' : 'false';

    for (const trigger of document.querySelectorAll(`[aria-controls="${dialogComponent.id}"]`)) {
      trigger.setAttribute('aria-expanded', expanded);
    }
  }

  /**
   * Points `aria-activedescendant` at the active option, or clears it when no option
   * is active.
   * @param {HTMLElement | null} item - The active option, if there is one.
   */
  #setActiveDescendant(item) {
    const { searchInput } = this.refs;

    if (item?.id) {
      searchInput.setAttribute('aria-activedescendant', item.id);
    } else {
      searchInput.removeAttribute('aria-activedescendant');
    }
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement)
      // Carousel results are rendered as `slideshow-slide` elements, and every slide
      // after index 0 carries `aria-hidden="true"` (see `snippets/slideshow-slide.liquid`).
      // An aria-hidden node must never be an option, be pointed at by
      // `aria-activedescendant`, or be reachable by arrow navigation.
      .filter((item) => !item.closest('[aria-hidden="true"]'));

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) {
      this.#setActiveDescendant(null);
      return;
    }

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    this.#setActiveDescendant(activeItem);

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    // Clear, Close, View all, and tabbed result links retain native key behavior.
    if (event.isComposing || event.target !== this.refs.searchInput) return;

    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    // Leave Enter to the native GET form while results belong to another query.
    if (!this.#hasCurrentResults || !this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      // No `case 'Tab':`. It used to preventDefault and move the active option,
      // which trapped Tab inside the result list so Clear, Close and View all were
      // unreachable by keyboard. Tab now follows natural DOM order, as it does in
      // `localization.js`. Arrow keys move the active option; that is the combobox
      // contract.

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          event.preventDefault();
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          // Match the form's hidden `options[prefix]=last` input so Enter
          // prefix-matches the same way the View All submit does.
          searchUrl.searchParams.set('options[prefix]', 'last');
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
        // The option set shrank, so ids have to be reassigned and `aria-expanded` /
        // `aria-activedescendant` re-synced against what is left.
        this.#decorateOptions();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = (event) => {
    this.resetSearch.cancel();
    this.#debouncedSearch.cancel();
    this.#activeFetch?.abort();
    this.#invalidateResults();
    const searchTerm = this.refs.searchInput.value.trim();
    if (searchTerm) this.#showResetButton();
    else this.#hideResetButton();
    // The final non-composing input event starts the search for IME users.
    if (event?.isComposing) return;
    if (!searchTerm) {
      this.#resetSearch();
      return;
    }
    this.#debouncedSearch(searchTerm);
  };

  #debouncedSearch = debounce((searchTerm) => {
    if (!this.isConnected || this.refs.searchInput.value.trim() !== searchTerm) return;
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    const deferredPromise = SearchUpdateEvent.createPromise();
    // The event may have no promise consumer; failures still stay contained here.
    deferredPromise.promise.catch(() => {});

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query: searchTerm,
        },
        promise: deferredPromise.promise,
      })
    );

    try {
      let resultsMarkup = await this.#fetchResultsMarkup(searchTerm, abortController.signal);

      // The engine misses some bare partial terms ("ber", "mag") that its
      // documented `*` partial-word syntax matches, so retry once with a
      // trailing wildcard when the bare query finds nothing. A `*` also
      // forces the whole query into strict prefix matching (it breaks
      // substring hits like "750mg"), so it must never be the first try.
      // The section strips the `*` back out of the rendered terms.
      if (
        resultsMarkup &&
        !searchTerm.endsWith('*') &&
        !abortController.signal.aborted &&
        this.#countResults(resultsMarkup) === 0
      ) {
        const wildcardMarkup = await this.#fetchResultsMarkup(`${searchTerm}*`, abortController.signal);
        if (wildcardMarkup && this.#countResults(wildcardMarkup) > 0) {
          resultsMarkup = wildcardMarkup;
        }
      }

      if (!resultsMarkup) {
        deferredPromise.resolve({ totalCount: 0 });
        return;
      }

      if (abortController.signal.aborted || !this.isConnected || this.refs.searchInput.value.trim() !== searchTerm) {
        deferredPromise.reject(new Error('Search superseded'));
        return;
      }

      morph(predictiveSearchResults, resultsMarkup);

      this.#revealResults(searchTerm);

      // Count all result items (products, collections, pages, articles, queries)
      const resultCount = predictiveSearchResults.querySelectorAll('[ref="resultsItems[]"]').length;
      deferredPromise.resolve({ totalCount: resultCount });
    } catch (error) {
      deferredPromise.reject(error);
      if (abortController.signal.aborted || !this.isConnected) return;
      // Suggestions are an enhancement. Keep the native search form available
      // when the suggestion endpoint fails instead of throwing from an event.
      if (this.#activeFetch === abortController) this.#invalidateResults();
      console.warn('[predictive-search] Suggestions unavailable:', error);
    }
  }

  /**
   * Fetch the rendered predictive search section markup for a query.
   * @param {string} query - The query to send to the predictive search endpoint
   * @param {AbortSignal} signal - The current query request signal
   * @returns {Promise<string | null>} The section markup
   */
  #fetchResultsMarkup(query, signal) {
    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[limit_scope]', 'each');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url, signal);
  }

  /**
   * Count result items (products, collections, pages, articles, queries) in
   * section markup without attaching it to the document.
   * @param {string} markup - The rendered section markup
   * @returns {number} The number of result items
   */
  #countResults(markup) {
    const template = document.createElement('template');
    template.innerHTML = markup;
    // Carousel resources (pages, collections, articles) carry only the card
    // class, not the ref — the same pairing #allResultsItems relies on.
    return template.content.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card').length;
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @param {AbortSignal} signal - The current reset request signal
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup(signal) {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url, signal);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    this.#debouncedSearch.cancel();
    this.#invalidateResults();
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    try {
      const emptySectionMarkup = await sectionRenderer.getSectionHTML(
        'predictive-search-empty', false, url, abortController.signal
      );
      if (abortController.signal.aborted || !this.isConnected || searchInput.value.trim()) return;
      const parsedEmptySectionMarkup = new DOMParser()
        .parseFromString(emptySectionMarkup, 'text/html')
        .querySelector('.predictive-search-empty-section');
      if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

      if (RecentlyViewed.getProducts().length > 0) {
        const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup(abortController.signal);
        if (recentlyViewedMarkup) {
          const recentProducts = new DOMParser()
            .parseFromString(recentlyViewedMarkup, 'text/html')
            .getElementById('predictive-search-products');
          const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
          if (recentProducts && collectionElement) {
            for (const child of recentProducts.children) {
              if (child instanceof HTMLElement) child.setAttribute('ref', 'recentlyViewedWrapper');
            }
            collectionElement.prepend(...recentProducts.children);
          }
        }
      }

      if (abortController.signal.aborted || !this.isConnected || searchInput.value.trim()) return;
      morph(predictiveSearchResults, parsedEmptySectionMarkup);
      this.#emptyStateLoaded = true;
      this.#revealResults('');
    } catch (error) {
      if (abortController.signal.aborted || !this.isConnected) return;
      this.#emptyStateLoaded = false;
      console.warn('[predictive-search] Default suggestions unavailable:', error);
    }
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
